import React from "react";

/**
 * Saor luxe vehicle picker — shared between the client dashboard and the
 * public/promo booking widget. Renders 3 tactile cards (Berline · Premium ·
 * VAN) with the elegant SVG silhouettes originally designed for the client
 * app. Selected card gets a colored border, gradient background and a
 * subtle "just picked" halo animation on tap.
 *
 * Usage:
 *   <VehicleCards value={vehicleType} onChange={setVehicleType} />
 */

// Elegant inline SVG silhouettes — Saor luxe aesthetic, no emoji.
export const VehicleIcons = {
  sedan: (props) => (
    <svg viewBox="0 0 64 32" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M3 22h2c0 2.2 1.8 4 4 4s4-1.8 4-4h22c0 2.2 1.8 4 4 4s4-1.8 4-4h2c1.7 0 3-1.3 3-3v-3.5c0-1.6-1-3-2.5-3.5l-9-3.2c-.8-.3-1.5-.7-2.1-1.3L31.4 7c-1-1-2.3-1.5-3.7-1.5H17.3c-1.5 0-3 .5-4.2 1.5L7.4 11.8c-.4.3-.9.5-1.4.6l-3.5.6C1.2 13.3 0 14.6 0 16.2V19c0 1.7 1.3 3 3 3z" fill="currentColor" opacity=".92"/>
      <circle cx="9" cy="22" r="2.6" fill="#0F2634"/>
      <circle cx="39" cy="22" r="2.6" fill="#0F2634"/>
    </svg>
  ),
  premium_sedan: (props) => (
    <svg viewBox="0 0 64 32" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M3 23h2c0 2.2 1.8 4 4 4s4-1.8 4-4h26c0 2.2 1.8 4 4 4s4-1.8 4-4h2c1.7 0 3-1.3 3-3v-3.8c0-1.7-1.1-3.2-2.7-3.7l-10-3.1c-.8-.3-1.6-.7-2.2-1.4L31.7 5.7C30.6 4.6 29.1 4 27.5 4H16.4c-1.7 0-3.3.6-4.6 1.8l-5.9 5.3c-.4.4-.9.6-1.5.7l-3.1.6C.6 12.6 0 13.5 0 14.5V20c0 1.7 1.3 3 3 3z" fill="currentColor"/>
      <circle cx="9" cy="23" r="2.8" fill="#0F2634"/>
      <circle cx="43" cy="23" r="2.8" fill="#0F2634"/>
      <path d="M16 7l1 4-3 1 2-5z" fill="#C9A86A" opacity=".95"/>
    </svg>
  ),
  van: (props) => (
    <svg viewBox="0 0 64 32" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path d="M3 24h2c0 2.2 1.8 4 4 4s4-1.8 4-4h30c0 2.2 1.8 4 4 4s4-1.8 4-4h2c1.7 0 3-1.3 3-3V11c0-2.2-1.8-4-4-4H10C7.2 7 5 9.2 5 12v.8L2 14C.8 14.4 0 15.5 0 16.8V21c0 1.7 1.3 3 3 3z" fill="currentColor" opacity=".92"/>
      <path d="M14 9.5h12v6H14V9.5zM28 9.5h12v6H28V9.5zM42 9.5h7c1.7 0 3 1.3 3 3v3H42V9.5z" fill="#fff" opacity=".22"/>
      <circle cx="9" cy="24" r="2.8" fill="#0F2634"/>
      <circle cx="45" cy="24" r="2.8" fill="#0F2634"/>
    </svg>
  ),
};

export const VEHICLE_OPTIONS = [
  { value: "sedan",         label: "Berline",  subtitle: "Tesla Model Y · 1-3 pers.", tag: null,        image: "/vehicles/sedan.png" },
  { value: "premium_sedan", label: "Premium",  subtitle: "Mercedes C-Class",          tag: "Signature", image: "/vehicles/premium_sedan.png" },
  { value: "van",           label: "VAN",      subtitle: "Mercedes V-Class · 8 pers.", tag: null,       image: "/vehicles/van.png" },
];

export default function VehicleCards({ value, onChange, testIdPrefix = "vehicle", showSubtitle = false }) {
  return (
    <div className="grid grid-cols-3 gap-2.5" data-testid={`${testIdPrefix}-cards`}>
      {VEHICLE_OPTIONS.map((o) => {
        const active = value === o.value;
        const isPremium = o.value === "premium_sedan";
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            data-testid={`${testIdPrefix}-${o.value}`}
            className={`group relative overflow-hidden rounded-2xl border p-3 pt-2.5 text-left transition-all duration-300 ${
              active
                ? isPremium
                  ? "border-[#C9A86A] bg-gradient-to-br from-[#FBF6EC] via-white to-white shadow-[0_2px_18px_-6px_rgba(201,168,106,0.45)]"
                  : "border-primary bg-gradient-to-br from-primary/[0.06] via-white to-white shadow-[0_2px_16px_-6px_hsl(var(--primary)/0.35)]"
                : "border-border bg-card hover:border-primary/30 hover:shadow-sm"
            }`}
          >
            {o.tag && (
              <span className="absolute right-2 top-2 z-10 rounded-full bg-[#C9A86A] px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.14em] text-white shadow-sm">
                {o.tag}
              </span>
            )}
            {/* Photorealistic 3D render — Nano Banana output. Aspect ratio
                held at 4/3 so the card row keeps a clean baseline across
                the three vehicles even though the underlying PNGs are
                square. `object-contain` prevents any awkward crop when
                the vehicle silhouette isn't perfectly centered. */}
            <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden">
              <img
                src={o.image}
                alt={o.label}
                className={`h-full w-full object-contain transition-transform duration-500 ease-out group-hover:scale-[1.04] ${
                  active ? "" : "opacity-90"
                }`}
                loading="lazy"
                draggable="false"
              />
            </div>
            <p className={`mt-1 font-[Outfit] text-[13px] font-semibold leading-tight tracking-tight ${active ? "text-foreground" : "text-foreground/85"}`}>
              {o.label}
            </p>
            {showSubtitle && (
              <p className="mt-0.5 text-[10px] leading-tight text-muted-foreground">{o.subtitle}</p>
            )}
            {active && (
              <span className={`absolute inset-x-3 bottom-0 h-[2px] rounded-t-full ${isPremium ? "bg-[#C9A86A]" : "bg-primary"}`} />
            )}
          </button>
        );
      })}
    </div>
  );
}
